import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import stringify from '../utils/stableStringify';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import AntTime from '../utils/AntTime';
import TileView from 'devextreme-react/tile-view';
import List from 'devextreme-react/list';

import { Template } from 'devextreme-react/core/template';
import AntConfig from '../utils/AntConfig';

import AntLocalization from '../utils/AntLocalization';
import AntCondition from '../utils/AntCondition';
import AntArray from '../utils/AntArray';
import { AntGlobal, AntSession } from '../utils/AntContext';

import AntCardListSkeleton from '../components/AntCardListSkeleton';
import useWidgetFirstLoad from '../components/AntWidgetSkeleton/useWidgetFirstLoad';
import AntCardViewCell from './AntPOSCardViewCell';
import AntCardViewUtil from './AntPOSCardViewUtil';

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    // const session = AntSession();
    const location = useLocation();
    const navigate = useNavigate();

    if (props.API && !props.API.entityDisplay) {
        if (appGlobal && appGlobal.appConfig && appGlobal.appConfig.Services) {
            let found = appGlobal.appConfig.Services.find((s) => s.name === props.API.entityName);
            if (found) props.API.init({ entityDisplay: found.display });
        }
    }

    const tableName = props.table ? props.table : props.API ? props.API.entityName : '';

    const config = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === tableName);
        if (found) return found.config;

        let cnf = new AntConfig(appGlobal, tableName);
        cnf.configFields(props.fields);

        if (appGlobal.tableConfigs instanceof Array === false) appGlobal.tableConfigs = [];

        appGlobal.tableConfigs.push({
            tableName: tableName,
            config: cnf
        });
        return cnf;
    }, []);

    const CardUtil = useMemo(() => {
        let util = new AntCardViewUtil(config, tableName);
        return util;
    }, []);

    const [listStates, setListStates] = useState({});

    const [activeItem, setActiveItem] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);

    const [listViewData, setListViewData] = useState(null);
    // Khung xám CHỈ cho lượt tải đầu tiên. Các lượt tải lại (socket, hẹn giờ, tính lại,
    // đổi bộ lọc) giữ nguyên thẻ đang hiển thị — không dựng lại khung xám.
    const [loaded, markLoaded] = useWidgetFirstLoad(true);

    const nonEmptyRowCount = useMemo(() => {
        if (!(listViewData instanceof Array)) return null;
        return listViewData.filter((row) => row && !row.empty).length;
    }, [listViewData]);

    // The watermark is only useful in the unused space below the final row.  Its
    // visibility therefore follows the actual viewport and rendered card height,
    // rather than a screen-specific number of cards or rows.
    const cardListHost = useRef(null);
    const [watermarkHasRoom, setWatermarkHasRoom] = useState(null);

    const updateWatermarkRoom = useCallback(() => {
        const host = cardListHost.current;
        if (!host || !props.bottomBackgroundImage) return;

        const list = host.querySelector('.ant-pos-card-list') || host;
        const viewport = list.querySelector('.dx-scrollable-container') || list;
        const cards = Array.from(list.querySelectorAll('.ant-card'));
        const viewportRect = viewport.getBoundingClientRect();

        if (!viewportRect.height || cards.length === 0) {
            setWatermarkHasRoom(null);
            return;
        }

        const watermarkFootprint = (Number(props.bottomBackgroundHeight) || 32) + (Number(props.bottomBackgroundBottom) || 8);
        const finalCardBottom = Math.max(...cards.map((card) => card.getBoundingClientRect().bottom));
        const hasRoom = finalCardBottom + watermarkFootprint <= viewportRect.bottom;

        setWatermarkHasRoom((current) => (current === hasRoom ? current : hasRoom));
    }, [props.bottomBackgroundImage, props.bottomBackgroundHeight, props.bottomBackgroundBottom]);

    useEffect(() => {
        if (!props.bottomBackgroundImage) return undefined;

        const host = cardListHost.current;
        if (!host) return undefined;

        const frame = window.requestAnimationFrame(updateWatermarkRoom);
        const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateWatermarkRoom);
        if (resizeObserver) resizeObserver.observe(host);

        return () => {
            window.cancelAnimationFrame(frame);
            if (resizeObserver) resizeObserver.disconnect();
        };
    }, [listViewData, props.bottomBackgroundImage, updateWatermarkRoom]);

    const canShowEmptyVisual = loaded && nonEmptyRowCount !== null;
    const showEmptyStateImage = canShowEmptyVisual && nonEmptyRowCount === 0 && Boolean(props.emptyStateImage);
    const showBottomBackground =
        canShowEmptyVisual &&
        Boolean(props.bottomBackgroundImage) &&
        (nonEmptyRowCount === 0 || watermarkHasRoom === true);

    // Đọc trạng thái chọn/rê chuột qua tham chiếu luôn-mới. Nhờ vậy các hàm gọi lại bên
    // dưới giữ nguyên danh tính, ô thẻ (đã bọc React.memo) không bị vẽ lại hàng loạt.
    const listStatesRef = useRef({});
    listStatesRef.current = listStates;

    // const activeItem = useRef();
    const listView = useRef();
    const showActionBarTimeout = useRef();
    const isScrolling = useRef(false);
    const isScrollingTimeout = useRef();

    const baseItemWidth = useRef(!props.colCount && props.baseItemWidth ? props.baseItemWidth : 100);

    const baseItemHeight = useRef(props.baseItemHeight ? props.baseItemHeight : 120);

    const [shouldRender, setShouldRender] = useState(1);

    const forceRender = useCallback(() => {
        setShouldRender((v) => v + 1);
    }, []);

    const isRender = useMemo(() => shouldRender + (props.shouldRender ? props.shouldRender : 1), [props.shouldRender, shouldRender]);

    const reload = useCallback(() => {
        if (listView.current && listView.current.instance) {
            // listView.current.instance.reload();
            // DevExtreme creates the List instance before its datasource in some
            // mount/reconfiguration paths. Do not let a pending datasource crash
            // the whole POS screen; the following normal reload will pick it up.
            const dataSource = listView.current.instance.getDataSource?.();
            if (!dataSource) return;

            dataSource.pageIndex(0);
            dataSource.reload();
            // props.dataSource.pageIndex(0);
            // props.dataSource.reload();
        }
    }, []);

    const repaintPatchedRows = useCallback(() => {
        // DevExtreme List keeps React item templates in its own render cache.
        // A store push updates the datasource, then this one-frame repaint makes
        // only the visible template layer consume that update. It is invoked for
        // socket patches only, never for periodic refreshes.
        window.requestAnimationFrame(() => {
            listView.current?.instance?.repaint?.();
        });
    }, []);

    const getRenderedRowCount = useCallback(() => {
        if (!loaded || !cardListHost.current) return null;
        return cardListHost.current.querySelectorAll('.dx-list-item').length;
    }, [loaded]);

    const onLoadData = useCallback((data, allData) => {
        // Decorations must follow the rows produced by the current query (including
        // a switcher/category filter), not the datasource's cached allData rows.
        // Some custom endpoints omit data but supply allData, hence the fallback.
        const rows = data instanceof Array ? data : allData instanceof Array ? allData : [];
        setListViewData((previous) => {
            // A lightweight patch can legitimately return the exact same row
            // objects. Avoid a List re-render in that case; this is especially
            // important for the POS table map, where a periodic redraw feels
            // like the whole grid is jumping.
            if (
                previous instanceof Array &&
                previous.length === rows.length &&
                previous.every((row, index) => row === rows[index])
            )
                return previous;
            return rows;
        });
        markLoaded();
    }, [markLoaded]);

    // const initBaseHeight = useCallback(() => {
    //     let height = props.baseItemHeight ? props.baseItemHeight : 0;

    //     let maxRowsCount = 0;
    //     if (listViewData instanceof Array) {
    //         for (let i = 0; i < listViewData.length; i++) {
    //             const item = listViewData[i];
    //             let count = CardUtil.getVisibileRowsCount({
    //                 props: props,
    //                 item: item,
    //                 cardLayout: props.cardLayout
    //             });
    //             if (count > maxRowsCount) maxRowsCount = count;
    //         }
    //     }

    //     if (maxRowsCount) height = maxRowsCount * 30 + 30;

    //     if (height < 100) height = 100;

    //     return height;
    // }, [props, listViewData, props.baseItemHeight]);

    const onAction = useCallback(
        async (e) => {
            let result;

            if (props.onAction) result = await props.onAction(e);

            setListStates((states) => {
                return {
                    ...states,
                    activeItem: null
                };
            });
            return result;
        },
        [props.onAction]
    );

    const onItemClick = useCallback(
        async (e) => {
            const target = e && e.event && e.event.target;
            let isOnActionButton = false;
            if (target && typeof target.closest === 'function') {
                if (
                    target.closest('.ant-card-cell-buttons') ||
                    target.closest('.ant-card-action-container') ||
                    target.closest('.dx-button') ||
                    target.closest('[data-card-no-nav]')
                ) {
                    isOnActionButton = true;
                }
            }

            // Some screens reuse the POS card renderer but need the mobile-style action
            // picker instead of the inline action strip or quick navigation.
            if (props.showActionsInPopup && props.onShowActions) {
                await props.onShowActions(e.itemData, props.cellActions, onAction);
                return;
            }

            if (props.useQuickAction) {
                if (props.cellActions instanceof Array && e.itemData && e.itemData.actions instanceof Array && e.itemData.actions.length > 0) {
                    let act = props.cellActions.find((c) => c.isQuickAction);
                    if (act) {
                        let act2 = e.itemData.actions.find((c) => c.name === act.code && c.visible === true);

                        if (act2) {
                            if (e.itemData.actions.filter((c) => c.visible === true).length === 1) {
                                onAction({ actionId: act2.name, action: act2.name, data: e.itemData });
                                return;
                            }
                        }
                    }
                }
            }

            if (props.showActions !== false) {
                if (e.itemData && e.itemData.actions instanceof Array && e.itemData.actions.length > 0) {
                    let newActiveItem = e.itemData;
                    // if (listStates.activeItem?.id === e.itemData.id) newActiveItem = null;

                    if (stringify(newActiveItem) !== stringify(listStatesRef.current.activeItem)) {
                        setListStates((states) => {
                            return {
                                ...states,
                                activeItem: props.showActions !== false ? newActiveItem : undefined,
                                selectedItem: e.itemData
                            };
                        });
                    }
                }
            }

            if (props.onItemClick) props.onItemClick(e.itemData, props.cellActions, { isOnActionButton });
        },
        [
            onAction,
            props.cellActions,
            props.onItemClick,
            props.showActions,
            props.rawDataSource,
            props.showActionsInPopup,
            props.onShowActions
        ]
    );

    const onItemMouseEnter = useCallback(
        async (e) => {
            if (!isScrolling.current) {
                if (e.itemData && e.itemData.actions instanceof Array && e.itemData.actions.length > 0) {
                    if (props.autoShowActions && props.showActions !== false) {
                        if (showActionBarTimeout.current) clearTimeout(showActionBarTimeout.current);
                        showActionBarTimeout.current = setTimeout(
                            (e) => {
                                setListStates((states) => {
                                    return {
                                        ...states,
                                        activeItem: e.itemData
                                    };
                                });
                            },
                            200,
                            e
                        );
                    }
                }

                if (props.onItemMouseEnter) {
                    props.onItemMouseEnter(e.itemData);
                }
            }
        },
        [props.onItemMouseEnter, props.autoShowActions, props.showActions, props.rawDataSource]
    );

    // const onItemMouseLeave = useCallback(async (e) => {
    //     // if (!isScrolling.current){
    //     //     console.log('ant-log-x-2 : ',);
    //     //     setListStates((states) => {
    //     //         return {
    //     //             ...states,
    //     //             activeItem: null
    //     //         };
    //     //     });
    //     // }

    //     // console.log('ant-log-onItemMouseLeave : ', e);
    // }, []);

    const onListMouseLeave = useCallback(async (e) => {
        setListStates((states) => {
            return {
                ...states,
                activeItem: null
            };
        });
    }, []);

    // const getCellStyle = useCallback(
    //     (itemData) => {
    //         if (props.cardStyles instanceof Array)
    //             for (let i = 0; i < props.cardStyles.length; i++) {
    //                 const cellStyle = props.cardStyles[i];

    //                 if (cellStyle.when && typeof cellStyle.when === 'object') {
    //                     var isValid = AntCondition.isValid(itemData, {
    //                         when: cellStyle.when,
    //                         whenType: cellStyle.whenType
    //                     });

    //                     if (isValid) return cellStyle;
    //                 }
    //             }
    //         return null;
    //     },
    //     [props.cardStyles]
    // );

    useImperativeHandle(ref, () => ({
        reload: reload,
        onLoadData: onLoadData,
        repaintPatchedRows: repaintPatchedRows,
        getRenderedRowCount: getRenderedRowCount
    }));

    const activeItemId = listStates.activeItem ? listStates.activeItem.id : null;
    const selectedItemId = listStates.selectedItem ? listStates.selectedItem.id : null;

    const itemlRender = useCallback(
        (data, i) => {
            let isActive = data && activeItemId != null && activeItemId === data.id;
            let isSelected = data && selectedItemId != null && selectedItemId === data.id;
            return (
                <AntCardViewCell
                    key={props.table + '-' + (data && data.id != null ? data.id : i)}
                    API={props.API}
                    // maxHeight={baseItemHeight.current ? baseItemHeight.current : 120}
                    table={props.table}
                    item={data}
                    itemIndex={i}
                    isActive={Boolean(isActive)}
                    isSelected={Boolean(isSelected)}
                    cardLayout={props.cardLayout}
                    cardStyles={props.cardStyles}
                    cardClasses={props.cardClasses}
                    cellActions={props.cellActions}
                    excludesRowActions={props.excludesRowActions}
                    showDetail={props.showActionDetail !== false ? props.showDetail : undefined}
                    hightLightSelectedItem={props.hightLightSelectedItem}
                    incrementStepperOnCardClick={props.incrementStepperOnCardClick}
                    onAction={onAction}
                    onMouseEnter={onItemMouseEnter}
                    // onMouseLeave={onItemMouseLeave}
                />
            );
        },
        [
            onAction,
            activeItemId,
            selectedItemId,
            onItemMouseEnter,
            // onItemMouseLeave,
            props.table,
            props.API,
            props.showDetail,
            props.cardLayout,
            props.cardStyles,
            props.cardClasses,
            props.cellActions,
            props.excludesRowActions,
            props.hightLightSelectedItem,
            props.incrementStepperOnCardClick
        ]
    );

    const onScroll = useCallback((e) => {
        isScrolling.current = true;
        if (isScrollingTimeout.current) clearTimeout(isScrollingTimeout.current);
        isScrollingTimeout.current = setTimeout(() => {
            isScrolling.current = false;
        }, 500);
    }, []);

    // const colCount = props.colCount ? props.colCount : null;

    let bgImage = appGlobal.appAssets.images.errorIcon1;

    return (
        <React.Fragment>
            {isRender && (
                <div
                    ref={cardListHost}
                    style={{
                        width: '100%',
                        height: props.usePageScroll ? 'auto' : '100%',
                        position: 'relative'
                    }}
                >
                    {showEmptyStateImage && (
                        <img
                            className="ant-pos-card-list-empty-hero"
                            src={props.emptyStateImage}
                            alt=""
                            aria-hidden="true"
                            draggable="false"
                            style={{ width: props.emptyStateImageSize || 300, opacity: props.emptyStateImageOpacity ?? 0.75 }}
                        />
                    )}
                    {showBottomBackground && (
                        <img
                            className="ant-pos-card-list-bottom-logo"
                            src={props.bottomBackgroundImage}
                            alt=""
                            aria-hidden="true"
                            draggable="false"
                            style={{ height: props.bottomBackgroundHeight || 32, bottom: props.bottomBackgroundBottom || 8 }}
                        />
                    )}
                    {!loaded && (
                        <AntCardListSkeleton
                            cardLayout={props.cardLayout}
                            cardClasses={props.cardClasses}
                            colCount={props.colCount}
                        />
                    )}
                    {/* <div
                        style={{
                            width: '100%',
                            height: '100%',

                            position: 'absolute',
                            backgroundImage: bgImage ? `url(${bgImage})` : undefined,

                            backgroundRepeat: 'no-repeat',
                            backgroundAttachment: 'fixed',
                            backgroundSize: '100% 100%',
                            filter: 'brightness(0.9)'
                        }}
                        className={`ant-app-backgroundImage`}
                    ></div> */}
                    {/* <div
                        style={{
                            display: listViewData instanceof Array && listViewData.length > 0 ? 'none' : 'flex',
                            justifyContent: 'center',
                            marginTop: 135
                        }}
                    >
                        <img
                            style={{
                                width: '40vh',
                                opacity: 0.5
                            }}
                            src={bgImage}
                        ></img>
                    </div> */}
                    <List
                        ref={listView}
                        {...props}
                        elementAttr={{
                            class: 'ant-pos-card-list'
                        }}
                        loadPanel={{ enabled: false }}
                        indicateLoading={false}
                        noDataText={''}
                        showScrollbar={props.usePageScroll ? 'never' : 'onHover'}
                        dataSource={props.dataSource}
                        scrollingEnabled={!props.usePageScroll}
                        activeStateEnabled={false}
                        focusStateEnabled={false}
                        hoverStateEnabled={false}
                        itemTemplate={'itemlRender'}
                        pageLoadingText={' '}
                        pageLoadMode={'scrollBottom'}
                        width={'100%'}
                        height={props.usePageScroll ? 'auto' : '100%'}
                        // itemMargin={0}
                        // useNativeScrolling={true}
                        scrollByContent={!props.usePageScroll}
                        scrollByThumb={!props.usePageScroll}
                        repaintChangesOnly={true}
                        onScroll={onScroll}
                        onItemClick={onItemClick}
                        onContentReady={(e) => {
                            e.element.addEventListener('mouseleave', onListMouseLeave);
                            window.requestAnimationFrame(updateWatermarkRoom);
                            if (props.onContentReady) props.onContentReady(e);
                            // const width = e.element.clientWidth;
                            // if (width && colCount) {
                            //     let oldBaseWidth = e.component.option('baseItemWidth');
                            //     let itemMargin = e.component.option('itemMargin');
                            //     let baseWidth = parseInt((width - itemMargin * colCount) / colCount);
                            //     if (baseWidth !== oldBaseWidth) {
                            //         baseItemWidth.current = baseWidth;
                            //         forceRender();
                            //     }
                            // }
                        }}
                        // onItemRendered={(e) => {
                        //     let style = getCellStyle(e.itemData);
                        //     if (style && style.backgroundColor) {
                        //         let ele = e.itemElement.querySelector('.ant-card');
                        //         console.log('ant-log-ele : ',ele);
                        //         if (ele) ele.style.backgroundColor = style.backgroundColor;
                        //     }
                        // }}
                    >
                        <Template name={'itemlRender'} render={itemlRender} />
                    </List>
                </div>
            )}
        </React.Fragment>
    );
});
